/**
 * Date/Time Utilities
 *
 * Provides date formatting, parsing, and calculation functions.
 */

// Timezone for Lebanon
const LEBANON_TIMEZONE = 'Asia/Beirut';

// Date format patterns
export const DATE_FORMATS = {
  short: 'MMM d, yyyy', // Jan 1, 2024
  long: 'MMMM d, yyyy', // January 1, 2024
  full: 'EEEE, MMMM d, yyyy', // Monday, January 1, 2024
  numeric: 'yyyy-MM-dd', // 2024-01-01
  display: 'dd/MM/yyyy', // 01/01/2024
  time: 'HH:mm', // 14:30
  datetime: 'MMM d, yyyy HH:mm', // Jan 1, 2024 14:30
} as const;

/**
 * Format a date for display
 */
export function formatDate(date: Date | string | number, format: keyof typeof DATE_FORMATS = 'short'): string {
  const d = toDate(date);

  const formatters: Record<keyof typeof DATE_FORMATS, Intl.DateTimeFormatOptions> = {
    short: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
    full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    numeric: { year: 'numeric', month: '2-digit', day: '2-digit' },
    display: { day: '2-digit', month: '2-digit', year: 'numeric' },
    time: { hour: '2-digit', minute: '2-digit', hour12: false },
    datetime: { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  };

  // Special handling for numeric format (yyyy-MM-dd)
  if (format === 'numeric') {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    ...formatters[format],
    timeZone: LEBANON_TIMEZONE,
  }).format(d);
}

/**
 * Parse a date string into a Date object
 */
export function parseDate(dateString: string): Date {
  // Handle ISO format
  if (dateString.includes('T') || dateString.includes('Z')) {
    return new Date(dateString);
  }

  // Handle yyyy-MM-dd format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle dd/MM/yyyy format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(dateString);
}

/**
 * Convert various input types to Date
 */
export function toDate(input: Date | string | number): Date {
  if (input instanceof Date) {
    return input;
  }
  if (typeof input === 'number') {
    return new Date(input);
  }
  return parseDate(input);
}

/**
 * Calculate number of nights between check-in and check-out
 */
export function calculateNights(checkIn: Date | string, checkOut: Date | string): number {
  const start = toDate(checkIn);
  const end = toDate(checkOut);

  // Reset time to midnight for accurate day calculation
  const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const diffTime = endMidnight.getTime() - startMidnight.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Check if a date is blocked
 */
export function isDateBlocked(
  date: Date | string,
  blockedDates: Array<{ start: Date | string; end: Date | string }>
): boolean {
  const d = toDate(date);

  for (const blocked of blockedDates) {
    const start = toDate(blocked.start);
    const end = toDate(blocked.end);

    if (d >= start && d <= end) {
      return true;
    }
  }

  return false;
}

/**
 * Get weekend dates within a date range
 * Weekend is Friday and Saturday in Lebanon
 */
export function getWeekendDates(
  checkIn: Date | string,
  checkOut: Date | string,
  weekendDays: number[] = [5, 6] // Friday = 5, Saturday = 6
): Date[] {
  const weekends: Date[] = [];
  const start = toDate(checkIn);
  const end = toDate(checkOut);

  const current = new Date(start);

  while (current < end) {
    if (weekendDays.includes(current.getDay())) {
      weekends.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }

  return weekends;
}

/**
 * Count weekend nights within a date range
 */
export function countWeekendNights(
  checkIn: Date | string,
  checkOut: Date | string,
  weekendDays: number[] = [5, 6]
): number {
  return getWeekendDates(checkIn, checkOut, weekendDays).length;
}

/**
 * Add days to a date
 */
export function addDays(date: Date | string, days: number): Date {
  const d = toDate(date);
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Subtract days from a date
 */
export function subtractDays(date: Date | string, days: number): Date {
  return addDays(date, -days);
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date | string, date2: Date | string): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Check if a date is today
 */
export function isToday(date: Date | string): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if a date is in the past
 */
export function isPast(date: Date | string): boolean {
  const d = toDate(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

/**
 * Check if a date is in the future
 */
export function isFuture(date: Date | string): boolean {
  const d = toDate(date);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return d > today;
}

/**
 * Get start of day
 */
export function startOfDay(date: Date | string): Date {
  const d = toDate(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/**
 * Get end of day
 */
export function endOfDay(date: Date | string): Date {
  const d = toDate(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Get date range for a month
 */
export function getMonthRange(year: number, month: number): { start: Date; end: Date } {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0); // Last day of month
  return { start, end };
}

/**
 * Format a date range for display
 */
export function formatDateRange(
  checkIn: Date | string,
  checkOut: Date | string
): string {
  const start = toDate(checkIn);
  const end = toDate(checkOut);

  // Same month
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(start);
    return `${month} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
  }

  // Same year
  if (start.getFullYear() === end.getFullYear()) {
    return `${formatDate(start, 'short')} - ${formatDate(end, 'short')}`;
  }

  // Different years
  return `${formatDate(start, 'short')} - ${formatDate(end, 'short')}`;
}

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 */
export function getRelativeTime(date: Date | string): string {
  const d = toDate(date);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (Math.abs(diffSecs) < 60) {
    return rtf.format(diffSecs, 'second');
  }
  if (Math.abs(diffMins) < 60) {
    return rtf.format(diffMins, 'minute');
  }
  if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day');
  }

  return formatDate(d, 'short');
}
